import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import MinimalFooter from "@/components/MinimalFooter";
import NeutralHeader from "@/components/NeutralHeader";
import ScoreGauge, { readinessBandFor } from "@/components/ScoreGauge";
import { submitLead } from "@/lib/api";
import { US_STATES } from "@/lib/questions";
import { CalendarClock, ArrowRight, CheckCircle2, AlertCircle, Landmark, PieChart, ShieldCheck, HeartPulse, ScrollText } from "lucide-react";
import { toast } from "sonner";

const CANONICAL_MEETING_URL = "https://meeting.levitate.ai/#/landing/ac7589-1a6g4x";
const _MEETING_URL_SENTINELS = new Set(["", "placeholder", "unset", "todo", "changeme"]);

function resolveMeetingUrl() {
  // Defensive: prefer REACT_APP_MEETING_URL from the deploy env, but ONLY when it
  // is a genuine, complete Levitate landing URL. Anything missing, sentinel, or
  // missing the /landing/ segment falls back to the canonical URL so a bad env
  // value can never redirect a visitor to https://meeting.levitate.ai/#/ (which
  // Levitate renders as "sorry...this is an invalid meeting url.").
  const raw = ((process.env.REACT_APP_MEETING_URL || "") + "").trim();
  if (_MEETING_URL_SENTINELS.has(raw.toLowerCase())) return CANONICAL_MEETING_URL;
  if (!raw.includes("/landing/")) return CANONICAL_MEETING_URL;
  return raw;
}

const MEETING_URL = resolveMeetingUrl();
const REDIRECT_INTENTS = new Set(["schedule_plan_review", "direct_discovery_meeting"]);

const CATEGORY_ORDER = ["tax", "income", "asset", "insurance", "legacy"];
const CATEGORY_LABEL = {
  tax: "Tax Planning",
  income: "Future Income & Retirement Planning",
  asset: "Asset Management",
  insurance: "Insurance & Protection",
  legacy: "Legacy & Estate Planning",
};
const CATEGORY_ICON = {
  tax: Landmark,
  income: PieChart,
  asset: ShieldCheck,
  insurance: HeartPulse,
  legacy: ScrollText,
};
const CATEGORY_TINT = {
  // Each category uses a tinted icon-circle. Colors chosen to meet AA contrast
  // against the pale background and remain distinct for color-blind viewers.
  tax: { bg: "#f3edf9", fg: "#5b3f8a" },
  income: { bg: "#eaf2fa", fg: "#1E1968" },
  asset: { bg: "#e8f3ec", fg: "#2f6b4a" },
  insurance: { bg: "#fdf4e4", fg: "#9a6a1f" },
  legacy: { bg: "#f9e9ea", fg: "#8f3b45" },
};

const isYoung = (answers) => answers?.retirement_status === "10plus";

// Life stage: "young" (>10y), "mid" (6–10y), "pre" (all other statuses incl. retired/unsure/<3y/3-5y)
const lifeStage = (answers) => {
  const s = answers?.retirement_status;
  if (s === "10plus") return "young";
  if (s === "6-10yr") return "mid";
  return "pre";
};

// -------- Copy dictionaries by life stage --------
const STRENGTH_COPY = {
  tax: {
    young: "Your responses suggest you're thinking about tax planning as part of your long-term wealth-building strategy.",
    mid: "Your responses suggest tax planning is being considered as retirement gets closer.",
    pre: "Your responses suggest tax planning is being considered as part of your retirement strategy.",
  },
  income: {
    young: "Your responses suggest you're beginning to think about how future income needs may be supported by savings and investments.",
    mid: "Your responses suggest you have started to identify how future retirement income will be supported.",
    pre: "Your responses suggest you have taken meaningful steps to identify your retirement income needs and potential income sources.",
  },
  asset: {
    young: "Your responses suggest that diversification, liquidity, and investment structure appear reasonably organized for long-term goals.",
    mid: "Your responses suggest that liquidity, diversification, and investment structure appear to be in reasonable shape as retirement approaches.",
    pre: "Your responses suggest that liquidity, diversification, and investment structure appear to be reasonably addressed.",
  },
  insurance: {
    young: "Your responses suggest that protection planning appears to be considered alongside your long-term goals.",
    mid: "Your responses suggest that protection planning appears to have been addressed as retirement gets closer.",
    pre: "Your responses suggest that protection and survivor planning appear to have been addressed.",
  },
  legacy: {
    young: "Your responses suggest that some legacy and estate-planning basics appear to be in place.",
    mid: "Several important legacy and estate-planning elements appear to be in place based on your answers.",
    pre: "Several important legacy and estate-planning elements appear to be in place based on your answers.",
  },
};

const REVIEW_COPY = {
  tax: {
    young: "Your responses suggest that tax diversification and long-term tax strategy may not yet be fully coordinated.",
    mid: "Your responses suggest that longer-term tax planning may benefit from more coordination as retirement approaches.",
    pre: "Your responses suggest that long-term retirement tax planning may not yet be fully coordinated.",
  },
  income: {
    young: "Your responses suggest that more work may be useful around clarifying long-term goals and how savings and investments are being built toward future needs.",
    mid: "Your responses suggest that more work may be useful around future income planning, spending needs, and withdrawal strategy as retirement approaches.",
    pre: "Your responses suggest that more work may be useful around retirement spending, income sources, withdrawal planning, or stress testing.",
  },
  asset: {
    young: "Your responses suggest that liquidity, diversification, investment risk, or account structure may deserve attention while you continue building wealth.",
    mid: "Your responses suggest that liquidity, diversification, investment risk, or account structure may benefit from a review as retirement approaches.",
    pre: "Your responses suggest that liquidity, diversification, investment risk, or account structure may deserve additional review.",
  },
  insurance: {
    young: "Your responses suggest that protection planning — including life, disability, or health-related coverage — may deserve a closer look.",
    mid: "Your responses suggest that long-term care, survivor planning, or other protection needs may deserve more attention as retirement gets closer.",
    pre: "Your responses suggest that long-term care, survivor planning, or insurance reviews may deserve more attention.",
  },
  legacy: {
    young: "Your responses suggest that basic estate documents and beneficiary designations may be worth putting in place.",
    mid: "Your responses suggest that estate documents, beneficiaries, or legacy intentions may deserve a review as retirement approaches.",
    pre: "Your responses suggest that estate documents, beneficiary designations, or legacy intentions may be worth reviewing.",
  },
};

const FLAG_COPY = {
  liquidity_review: {
    young: "Your answers suggest that short-term liquidity may be worth reviewing so unexpected costs don't disrupt long-term investing.",
    mid: "Your answers suggest that access to short-term funds may be worth reviewing as retirement gets closer.",
    pre: "Your answers suggest that access to short-term funds may be worth reviewing.",
  },
  concentration_review: {
    young: "Your investments may have meaningful concentration — worth reviewing while your time horizon is long.",
    mid: "Your portfolio may have meaningful concentration as retirement approaches.",
    pre: "Your portfolio may have meaningful concentration based on your response.",
  },
  rmd_review: {
    young: "Future required distribution rules on tax-deferred accounts may be worth understanding as balances grow.",
    mid: "Future required distributions may be a useful topic to think about as retirement approaches.",
    pre: "Future required distributions may be a useful topic to review.",
  },
  roth_review: {
    young: "Roth contributions and long-term tax diversification may be worth exploring while your income and time horizon are favorable.",
    mid: "Roth planning and tax diversification may be worth reviewing as retirement approaches.",
    pre: "Roth conversion planning has not yet been fully evaluated based on your response.",
  },
  withdrawal_review: {
    young: "How your different accounts may be used across future goals could be worth exploring.",
    mid: "A coordinated approach for future account withdrawals may deserve more thought.",
    pre: "A coordinated withdrawal strategy across accounts may deserve attention.",
  },
  ltc_review: {
    young: "How future extended-care costs might be handled may be worth thinking about, even at a distance.",
    mid: "How extended-care costs might be handled may deserve some early thought.",
    pre: "How extended-care costs would be handled may be worth reviewing.",
  },
  emergency_protection_review: {
    young: "How your household would handle an unexpected income interruption may deserve some early planning.",
    mid: "Income-protection preparedness may deserve a review as retirement approaches.",
    pre: "Income-protection preparedness may deserve a review.",
  },
  beneficiary_review: {
    young: "Beneficiary designations on accounts and policies may benefit from a review.",
    mid: "Beneficiary designations may benefit from a recent review.",
    pre: "Beneficiary designations may benefit from a recent review.",
  },
  estate_documents_review: {
    young: "Core estate documents (will, powers of attorney) may not yet be in place — worth putting on the list.",
    mid: "Core estate documents may benefit from being reviewed or completed as retirement approaches.",
    pre: "Core estate documents may not yet be fully in place.",
  },
  debt_review: {
    young: "Your responses suggest that debt obligations may be limiting flexibility for saving, investing, or other financial goals.",
    mid: "Your responses suggest that debt obligations may be limiting flexibility as retirement approaches.",
    pre: "Your responses suggest that debt obligations may be limiting flexibility for retirement spending or other financial goals.",
  },
  cashflow_awareness: {
    young: "Understanding where money is currently going can make it easier to evaluate savings, investing, liquidity, and future goals.",
    mid: "Understanding where money is currently going can make it easier to plan for retirement spending and future needs.",
    pre: "Understanding current household spending can help clarify how retirement income will need to be structured.",
  },
  long_term_saving: {
    young: "Your responses suggest that building a more consistent approach to long-term saving and investing may be worth exploring.",
    mid: "Your responses suggest that increasing consistent long-term saving may be worth exploring as retirement approaches.",
    pre: "Your responses suggest that consistent saving may be worth exploring.",
  },
  rising_costs: {
    young: "Your responses suggest that future increases in living costs may not yet be fully reflected in your long-term planning.",
    mid: "Your responses suggest that future increases in living costs may not yet be fully reflected in your long-term planning.",
    pre: "Your responses suggest that rising living costs may deserve additional attention when reviewing future retirement income needs.",
  },
  inflation_review: {
    young: "Your responses suggest that future increases in living costs may not yet be fully reflected in your long-term planning.",
    mid: "Your responses suggest that future increases in living costs may not yet be fully reflected in your planning.",
    pre: "Your responses suggest that rising living costs may deserve additional attention when reviewing future retirement income needs.",
  },
};

const CTA_COPY = {
  young: "Your score is designed to highlight areas that may deserve additional attention. If you'd like help reviewing your long-term strategy and understanding how the different pieces of your financial life fit together, you can speak with our planning team.",
  mid: "Your score is designed to highlight areas that may deserve additional attention. If you'd like help reviewing your strategy as retirement gets closer, you can speak with our planning team.",
  pre: "Your score is designed to highlight areas that may deserve additional attention. If you'd like help reviewing your current strategy and understanding how the different pieces of your plan work together, you can speak with our planning team.",
};

const pick = (dict, key, stage) => (dict[key] && dict[key][stage]) || null;

export default function Results() {
  const navigate = useNavigate();
  const [payload, setPayload] = useState(null);
  const [leadIntent, setLeadIntent] = useState("general_contact");

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("summit_trail_result");
      if (!raw) { navigate("/"); return; }
      setPayload(JSON.parse(raw));
    } catch { navigate("/"); }
  }, [navigate]);

  if (!payload) return null;

  const { result, answers } = payload;
  const scores = result.category_scores;
  const maxes = result.category_max;
  const stage = lifeStage(answers);
  const strengthCopy = (s) => pick(STRENGTH_COPY, s.key, stage) || s.copy;
  const reviewCopy = (s) => pick(REVIEW_COPY, s.key, stage) || s.copy;
  const flagCopy = (f) => pick(FLAG_COPY, f.key, stage) || f.copy;
  const ctaSupport = CTA_COPY[stage];

  const handleReviewMyPlan = (e) => {
    e.preventDefault();
    setLeadIntent("schedule_plan_review");
    document.getElementById("contact-section")?.scrollIntoView({ behavior: "smooth" });
  };

  const handleScheduleMeeting = (e) => {
    e.preventDefault();
    setLeadIntent("direct_discovery_meeting");
    document.getElementById("contact-section")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="min-h-screen flex flex-col">
      <NeutralHeader showSubtitle={false} />

      <main id="main-content" tabIndex={-1} className="flex-1" data-testid="results-main">

      {/* SNAPSHOT HERO — atmospheric scrim, donut score, couple image overlay */}
      <section className="atmo-mountain">
        <div className="summit-container py-14 lg:py-20 grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-7">
            <div className="section-pill mb-5" data-testid="results-eyebrow">Your Results</div>
            <h1
              className="font-serif"
              style={{ color: "#1E1968", fontSize: "clamp(2.2rem, 4.5vw, 3.5rem)", fontWeight: 600, lineHeight: 1.04, letterSpacing: "-0.015em" }}
              data-testid="results-title"
            >
              Your Financial Future<br />Readiness Snapshot
            </h1>
            <p className="mt-5 text-lg" style={{ color: "#17233D", maxWidth: 580, lineHeight: 1.55 }}>
              Here&rsquo;s a summary of your results based on your responses. This snapshot highlights your overall financial readiness
              and shows how you&rsquo;re doing across five key planning areas.
            </p>
          </div>

          {/* Donut score card — premium navy treatment with band-color accent */}
          <div className="lg:col-span-5">
            <div
              style={{
                background: "#1E1968",
                color: "#ffffff",
                padding: 0,
                boxShadow: "0 18px 42px rgba(23, 35, 61, 0.22)",
                overflow: "hidden",
                position: "relative",
              }}
              data-testid="score-card"
            >
              {/* Band color bar at the top */}
              <div
                aria-hidden="true"
                style={{ height: 6, background: readinessBandFor(result.trail_score).color }}
              />
              <div style={{ padding: "2rem 1.75rem 1.75rem" }}>
                <div
                  className="text-xs"
                  style={{ color: "#9fb5cf", fontWeight: 600, letterSpacing: "0.22em", textTransform: "uppercase" }}
                >
                  Overall Readiness Score
                </div>

                <div className="mt-6 flex justify-center">
                  <Donut
                    score={result.trail_score}
                    max={100}
                    trackColor="rgba(255,255,255,0.14)"
                    progressColor={readinessBandFor(result.trail_score).color}
                    valueColor="#ffffff"
                    captionColor="#9fb5cf"
                  />
                </div>

                {/* Band badge */}
                <div className="mt-6 flex justify-center" data-testid="band-label">
                  <span
                    className="font-serif inline-flex items-center gap-2"
                    style={{
                      background: "rgba(255,255,255,0.08)",
                      border: "1px solid rgba(255,255,255,0.18)",
                      padding: "0.5rem 0.95rem",
                      color: "#ffffff",
                      fontSize: "1.05rem",
                      fontWeight: 600,
                      lineHeight: 1.1,
                    }}
                  >
                    <span
                      aria-hidden="true"
                      style={{
                        display: "inline-block",
                        width: 10, height: 10, borderRadius: 999,
                        background: readinessBandFor(result.trail_score).color,
                      }}
                    />
                    {readinessBandFor(result.trail_score).label}
                  </span>
                </div>

                <p
                  className="mt-5 text-sm"
                  style={{ color: "#dce7f5", lineHeight: 1.65, textAlign: "center", maxWidth: 360, margin: "1.25rem auto 0 auto" }}
                  data-testid="band-copy"
                >
                  {result.band.copy}
                </p>

                <div
                  className="mt-6 pt-5 text-xs"
                  style={{ borderTop: "1px solid rgba(255,255,255,0.12)", color: "#9fb5cf", textAlign: "center", lineHeight: 1.5 }}
                >
                  Educational score based on your responses &middot; Not a financial recommendation
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* COMPACT CTA (immediately after score) */}
      <section style={{ background: "#EAF2FA", borderBottom: "1px solid #d5e3f2" }} data-testid="compact-cta">
        <div className="summit-container py-8 lg:py-10 grid lg:grid-cols-12 gap-6 items-center">
          <div className="lg:col-span-8">
            <h3 className="font-serif" style={{ color: "#1E1968", fontSize: "1.35rem", fontWeight: 600, lineHeight: 1.25 }}>
              Want help understanding your results?
            </h3>
            <p className="mt-2 text-base" style={{ color: "#17233D", maxWidth: 640, lineHeight: 1.6 }}>
              Your score highlights areas that may be worth a closer look. If you&rsquo;d like, a planning professional can help you review
              how the different pieces of your financial life fit together.
            </p>
          </div>
          <div className="lg:col-span-4 flex flex-col lg:items-end gap-2">
            <a href="#contact-section" onClick={handleReviewMyPlan} className="btn-primary" data-testid="compact-review-cta">
              Review My Plan <ArrowRight className="ml-2 w-4 h-4" aria-hidden="true" />
            </a>
            <a
              href="#contact-section"
              onClick={handleScheduleMeeting}
              className="btn-secondary"
              data-testid="compact-schedule-cta"
            >
              <CalendarClock className="w-4 h-4 mr-2" aria-hidden="true" /> Schedule a Discovery Meeting
            </a>
            <div className="text-xs" style={{ color: "#3a4d70" }} data-testid="compact-cta-attribution">
              Plan reviews are provided by Summit Retirement Solutions
            </div>
          </div>
        </div>
      </section>

      {/* A CLOSER LOOK AT YOUR RESULTS — category cards */}
      <section style={{ background: "#eaf2fa" }}>
        <div className="summit-container py-14 lg:py-16">
          <div className="grid lg:grid-cols-12 gap-8 items-end">
            <div className="lg:col-span-6">
              <div className="text-xs uppercase tracking-[0.16em] mb-2" style={{ color: "#3a4d70", fontWeight: 600 }}>
                Your Scores by Planning Area
              </div>
              <h2 className="font-serif" style={{ color: "#1E1968", fontSize: "clamp(1.75rem, 3vw, 2.3rem)", fontWeight: 600, lineHeight: 1.15 }}>
                A Closer Look at Your Results
              </h2>
            </div>
            <div className="lg:col-span-6">
              <p className="text-base" style={{ color: "#17233D", lineHeight: 1.65, maxWidth: 560 }}>
                Your score in each area reflects how your answers compare to key planning best practices. This can help you see where
                you&rsquo;re well prepared and where you may want to focus next.
              </p>
            </div>
          </div>

          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-5 gap-5" data-testid="category-breakdown">
            {CATEGORY_ORDER.map((k) => (
              <CategoryCard
                key={k}
                label={CATEGORY_LABEL[k]}
                score={scores[k]}
                max={maxes[k]}
                testKey={k}
                icon={CATEGORY_ICON[k]}
                tint={CATEGORY_TINT[k]}
              />
            ))}
          </div>

          {/* STRENGTHS + REVIEW panels (color-coded) */}
          <div className="mt-10 grid lg:grid-cols-2 gap-6">
            {/* Strengths */}
            <div
              style={{ background: "#edf7ef", border: "1px solid #cfe6d4", padding: "1.5rem 1.5rem" }}
              data-testid="strengths-panel"
            >
              <div className="flex items-center gap-3">
                <span
                  aria-hidden="true"
                  style={{
                    width: 36, height: 36, borderRadius: 999, background: "#2f6b4a",
                    display: "inline-flex", alignItems: "center", justifyContent: "center", color: "#fff",
                  }}
                >
                  <CheckCircle2 style={{ width: 20, height: 20 }} strokeWidth={2} />
                </span>
                <h3 className="font-serif" style={{ color: "#1E1968", fontSize: "1.25rem", fontWeight: 600, lineHeight: 1.25 }}>
                  Areas That Appear Well Organized
                </h3>
              </div>
              <p className="mt-3 text-sm" style={{ color: "#2d4a35", lineHeight: 1.6 }}>
                Based on your responses, these areas are relative strengths.
              </p>
              <ul className="mt-4 space-y-2.5" data-testid="strengths-list" style={{ listStyle: "none", padding: 0, margin: 0 }}>
                {result.strengths.length === 0 && (
                  <li className="text-base" style={{ color: "#2d4a35" }}>
                    Your responses suggest there may be opportunities for greater coordination across several planning areas.
                  </li>
                )}
                {result.strengths.map((s) => (
                  <li key={s.key} className="flex gap-2.5 items-start text-base" style={{ color: "#17233D", lineHeight: 1.55 }} data-testid={`strength-${s.key}`}>
                    <CheckCircle2 className="w-4 h-4 mt-1" style={{ color: "#2f6b4a", flex: "0 0 auto" }} strokeWidth={2.2} aria-hidden="true" />
                    <span><strong style={{ color: "#1E1968" }}>{s.label}.</strong> {strengthCopy(s)}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Review areas */}
            <div
              style={{ background: "#fdf6e8", border: "1px solid #e9d9ba", padding: "1.5rem 1.5rem" }}
              data-testid="review-panel"
            >
              <div className="flex items-center gap-3">
                <span
                  aria-hidden="true"
                  style={{
                    width: 36, height: 36, borderRadius: 999, background: "#c18a2a",
                    display: "inline-flex", alignItems: "center", justifyContent: "center", color: "#fff",
                  }}
                >
                  <AlertCircle style={{ width: 20, height: 20 }} strokeWidth={2} />
                </span>
                <h3 className="font-serif" style={{ color: "#1E1968", fontSize: "1.25rem", fontWeight: 600, lineHeight: 1.25 }}>
                  Areas That May Deserve a Closer Look
                </h3>
              </div>
              <p className="mt-3 text-sm" style={{ color: "#6b4a1a", lineHeight: 1.6 }}>
                These areas may benefit from additional planning or review.
              </p>
              <ul className="mt-4 space-y-2.5" data-testid="review-areas-list" style={{ listStyle: "none", padding: 0, margin: 0 }}>
                {result.review_areas.map((s) => (
                  <li key={s.key} className="flex gap-2.5 items-start text-base" style={{ color: "#17233D", lineHeight: 1.55 }} data-testid={`review-${s.key}`}>
                    <AlertCircle className="w-4 h-4 mt-1" style={{ color: "#c18a2a", flex: "0 0 auto" }} strokeWidth={2.2} aria-hidden="true" />
                    <span><strong style={{ color: "#1E1968" }}>{s.label}.</strong> {reviewCopy(s)}</span>
                  </li>
                ))}
              </ul>
              {result.flags?.length > 0 && (
                <div className="mt-5 pt-5" style={{ borderTop: "1px solid #e9d9ba" }}>
                  <div className="text-xs uppercase tracking-[0.14em] mb-2.5" style={{ color: "#6b4a1a", fontWeight: 600 }}>Questions worth exploring</div>
                  <ul className="space-y-2" style={{ listStyle: "none", padding: 0, margin: 0 }}>
                    {result.flags.map((f) => (
                      <li key={f.key} className="text-sm flex gap-2 items-start" style={{ color: "#17233D", lineHeight: 1.55 }} data-testid={`flag-${f.key}`}>
                        <span style={{ color: "#c18a2a" }}>•</span>
                        <span>{flagCopy(f)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* PRIMARY CTA — first Summit reveal */}
      <section id="review-my-plan" style={{ background: "#1E1968", color: "#fff" }}>
        <div className="summit-container py-16 lg:py-20 grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-8">
            <div className="text-xs uppercase tracking-[0.16em] mb-4" style={{ color: "#8fbde0" }} data-testid="cta-attribution-eyebrow">
              You don't have to figure this out alone
            </div>
            <h2 className="font-serif" style={{ fontSize: "clamp(1.75rem, 3vw, 2.5rem)", fontWeight: 500, lineHeight: 1.15 }}>
              Don't worry, you don't have to figure this out alone.
            </h2>
            <p className="mt-5 text-lg" style={{ color: "#c7d6e8", maxWidth: 680 }}>
              {ctaSupport}
            </p>
          </div>
          <div className="lg:col-span-4 flex flex-col lg:items-end gap-3">
            <a href="#contact-section" onClick={handleReviewMyPlan} className="btn-primary" style={{ background: "#39A5C8", borderColor: "#39A5C8" }} data-testid="primary-review-cta">
              Review My Plan <ArrowRight className="ml-2 w-4 h-4" />
            </a>
            <a
              href="#contact-section"
              onClick={handleScheduleMeeting}
              data-testid="primary-schedule-cta"
              style={{
                display: "inline-flex", alignItems: "center", justifyContent: "center",
                background: "transparent", color: "#fff",
                border: "1px solid rgba(255,255,255,0.55)",
                padding: "0.7rem 1.1rem", fontSize: "0.95rem", letterSpacing: "0.02em",
                textDecoration: "none",
              }}
            >
              <CalendarClock className="w-4 h-4 mr-2" /> Schedule a Discovery Meeting
            </a>
            <div className="text-sm" style={{ color: "#8fbde0" }} data-testid="cta-attribution">
              Plan reviews are provided by Summit Retirement Solutions
            </div>
          </div>
        </div>
      </section>

      {/* CONTACT FORM */}
      <ContactSection answers={answers} leadIntent={leadIntent} />

      {/* DISCLOSURE */}
      <section style={{ background: "#F5F8FC" }}>
        <div className="summit-container py-12">
          <h3 className="font-serif" style={{ color: "#173A6A", fontSize: "1.15rem", fontWeight: 600 }}>About Your Score</h3>
          <p className="mt-3 text-sm" style={{ color: "#3a4d70", maxWidth: 900 }}>
            This educational assessment is based solely on the information you provide. It is intended to highlight
            planning topics that may be worth reviewing and is not a financial plan, investment recommendation, tax opinion,
            legal opinion, insurance recommendation, or guarantee of future results. A complete review would require additional
            information about your individual circumstances.
          </p>
        </div>
      </section>

      </main>
      <MinimalFooter />
    </div>
  );
}

function Donut({ score, max, trackColor = "#eaf2fa", progressColor = "#258FC4", valueColor = "#1E1968", captionColor = "#3a4d70" }) {
  const safeMax = Math.max(1, max || 100);
  const pct = Math.max(0, Math.min(1, (score || 0) / safeMax));
  const size = 200;
  const stroke = 16;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const offset = c * (1 - pct);
  return (
    <div style={{ position: "relative", width: size, height: size }} data-testid="score-donut">
      <svg width={size} height={size} role="img" aria-label={`Readiness score ${Math.round(score)} out of ${safeMax}`}>
        <circle cx={size/2} cy={size/2} r={r} stroke={trackColor} strokeWidth={stroke} fill="none" />
        <circle
          cx={size/2} cy={size/2} r={r}
          stroke={progressColor} strokeWidth={stroke} fill="none"
          strokeDasharray={c} strokeDashoffset={offset}
          strokeLinecap="round"
          transform={`rotate(-90 ${size/2} ${size/2})`}
          style={{ transition: "stroke-dashoffset 800ms ease-out" }}
        />
      </svg>
      <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
        <div className="font-serif" style={{ color: valueColor, fontSize: "3rem", fontWeight: 600, lineHeight: 1 }} data-testid="score-value">
          {Math.round(score)}
        </div>
        <div className="text-xs mt-1.5" style={{ color: captionColor, letterSpacing: "0.1em" }}>/ {safeMax}</div>
      </div>
    </div>
  );
}

function CategoryCard({ label, score, max, testKey, icon: Icon, tint }) {
  const safeMax = Math.max(1, max || 1);
  const pct = Math.max(0, Math.min(100, Math.round(((score || 0) / safeMax) * 100)));
  return (
    <div
      style={{ background: "#ffffff", border: "1px solid #e0e8f2", padding: "1.25rem 1.25rem" }}
      data-testid={`category-${testKey}`}
    >
      <div className="flex items-start gap-3">
        <span
          aria-hidden="true"
          style={{
            flex: "0 0 auto",
            width: 40, height: 40,
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            background: tint.bg, color: tint.fg,
            borderRadius: 999,
          }}
        >
          <Icon style={{ width: 20, height: 20 }} strokeWidth={1.7} />
        </span>
        <div className="font-serif" style={{ color: "#1E1968", fontSize: "0.98rem", fontWeight: 600, lineHeight: 1.25, paddingTop: 4 }}>
          {label}
        </div>
      </div>
      <div className="mt-4 flex items-baseline gap-1" data-testid={`category-bar-${testKey}`}>
        <span className="font-serif" style={{ color: tint.fg, fontSize: "1.9rem", fontWeight: 700, lineHeight: 1 }} data-testid={`category-score-${testKey}`}>
          {Math.round(score)}
        </span>
        <span className="text-sm" style={{ color: "#3a4d70" }}>/ {safeMax}</span>
      </div>
      <div className="mt-2 w-full h-1.5" style={{ background: "#eaf2fa" }} role="presentation">
        <div style={{ width: `${pct}%`, height: "100%", background: tint.fg, transition: "width 600ms ease" }} />
      </div>
    </div>
  );
}

function ContactSection({ answers, leadIntent }) {
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", phone: "",
    contact_method: "email", state: "", best_time: "no_preference", consent: false,
  });
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);

  const change = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const validate = () => {
    if (!form.first_name.trim()) return "First name is required.";
    if (!form.last_name.trim()) return "Last name is required.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return "Please enter a valid email.";
    if (!form.state) return "Please select a state.";
    if (form.contact_method === "phone" && !form.phone.trim()) return "Phone number is required when preferred method is Phone.";
    if (!form.consent) return "Please provide consent to proceed.";
    return null;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (busy) return;
    const err = validate();
    if (err) { toast.error(err); return; }
    setBusy(true);
    try {
      await submitLead(answers, form, leadIntent);
      if (REDIRECT_INTENTS.has(leadIntent)) {
        toast.success("Thank you — redirecting you to scheduling…");
        window.location.href = MEETING_URL;
        return;
      }
      setSubmitted(true);
      toast.success("Thank you — your information has been received.");
    } catch (e) {
      const msg = e?.response?.data?.detail || "We couldn't submit your information. Please try again.";
      toast.error(msg);
      setBusy(false);
      return;
    }
    setBusy(false);
  };

  if (submitted) {
    return (
      <section style={{ background: "#fff" }} data-testid="contact-thank-you">
        <div className="summit-container py-16 text-center">
          <CheckCircle2 className="w-10 h-10 mx-auto" style={{ color: "#258FC4" }} strokeWidth={1.5} />
          <h2 className="font-serif mt-4" style={{ color: "#1E1968", fontSize: "2rem", fontWeight: 600 }}>Thank You</h2>
          <p className="mt-3 text-base" style={{ color: "#3a4d70", maxWidth: 560, margin: "0.75rem auto 0" }}>
            Your information has been received. A member of the Summit Retirement Solutions team can follow up with you.
          </p>
          <div className="mt-8">
            <div className="text-sm" style={{ color: "#173A6A" }}>Prefer to choose a time now?</div>
            <a href={MEETING_URL} target="_blank" rel="noreferrer" className="btn-primary mt-3" data-testid="ty-schedule-btn">
              <CalendarClock className="w-4 h-4 mr-2" /> Schedule My Meeting
            </a>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="contact-section" style={{ background: "#fff" }} data-testid="contact-form-section">
      <div className="summit-container py-16 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5">
          <div className="section-pill mb-4" data-testid="contact-eyebrow">
            {leadIntent === "direct_discovery_meeting"
              ? "Schedule a Discovery Meeting"
              : leadIntent === "schedule_plan_review"
                ? "Review My Plan"
                : "Prefer that we contact you?"}
          </div>
          <h2 className="font-serif" style={{ color: "#1E1968", fontSize: "1.75rem", fontWeight: 500, lineHeight: 1.2 }} data-testid="contact-heading">
            {leadIntent === "direct_discovery_meeting"
              ? "Enter your information and continue directly to scheduling."
              : leadIntent === "schedule_plan_review"
                ? "Leave your information and a member of the Summit Retirement Solutions team will reach out."
                : "Leave your information and a member of the Summit Retirement Solutions team will reach out."}
          </h2>
          <p className="mt-4 text-base" style={{ color: "#3a4d70" }} data-testid="contact-subcopy">
            {leadIntent === "direct_discovery_meeting"
              ? "Your assessment results will be saved with your request so the planning team can review them before your conversation. You'll be taken to our scheduling page after you submit."
              : leadIntent === "schedule_plan_review"
                ? "If you'd like help understanding your results, submit your information below and the planning team can review your assessment with you. You'll be taken to our scheduling page after you submit."
                : "No obligation. Your details are used only to help schedule a conversation about the results of your assessment."}
          </p>
          <p className="mt-4 text-sm" style={{ color: "#3a4d70", background: "#F5F8FC", padding: "0.85rem 1rem", borderLeft: "3px solid #258FC4" }} data-testid="contact-privacy-note">
            Your information is used only to respond to your request and review the assessment you chose to submit. Please do not include
            account numbers, Social Security numbers, passwords, or other highly sensitive information.
          </p>
        </div>
        <form className="lg:col-span-7 space-y-5" onSubmit={onSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <FormField label="First name *">
              <input className="summit-input" value={form.first_name} onChange={(e) => change("first_name", e.target.value)} data-testid="contact-first-name" />
            </FormField>
            <FormField label="Last name *">
              <input className="summit-input" value={form.last_name} onChange={(e) => change("last_name", e.target.value)} data-testid="contact-last-name" />
            </FormField>
          </div>
          <FormField label="Email *">
            <input type="email" className="summit-input" value={form.email} onChange={(e) => change("email", e.target.value)} data-testid="contact-email" />
          </FormField>
          <FormField label={`Phone${form.contact_method === "phone" ? " *" : ""}`}>
            <input className="summit-input" value={form.phone} onChange={(e) => change("phone", e.target.value)} placeholder="(555) 123-4567" data-testid="contact-phone" />
          </FormField>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <FormField label="Preferred contact method *">
              <select className="summit-input" value={form.contact_method} onChange={(e) => change("contact_method", e.target.value)} data-testid="contact-method">
                <option value="phone">Phone</option>
                <option value="email">Email</option>
                <option value="either">Either</option>
              </select>
            </FormField>
            <FormField label="State *">
              <select className="summit-input" value={form.state} onChange={(e) => change("state", e.target.value)} data-testid="contact-state">
                <option value="">Select a state</option>
                {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </FormField>
          </div>
          <FormField label="Best time to contact">
            <select className="summit-input" value={form.best_time} onChange={(e) => change("best_time", e.target.value)} data-testid="contact-time">
              <option value="morning">Morning</option>
              <option value="afternoon">Afternoon</option>
              <option value="evening">Evening</option>
              <option value="no_preference">No preference</option>
            </select>
          </FormField>
          <label className="flex items-start gap-3 text-sm" style={{ color: "#3a4d70" }}>
            <input type="checkbox" checked={form.consent} onChange={(e) => change("consent", e.target.checked)} className="mt-1" data-testid="contact-consent" />
            <span>I agree that Summit Retirement Solutions may contact me using the information provided.</span>
          </label>
          <button type="submit" className="btn-primary" disabled={busy} data-testid="contact-submit">
            {busy ? "Submitting…"
              : leadIntent === "direct_discovery_meeting" ? "Continue to Scheduling"
              : leadIntent === "schedule_plan_review" ? "Request a Plan Review"
              : "Request a Plan Review"}
          </button>
        </form>
      </div>
    </section>
  );
}

function FormField({ label, children }) {
  return (
    <label className="block">
      <span className="block text-sm mb-2" style={{ color: "#173A6A", fontWeight: 500 }}>{label}</span>
      {children}
    </label>
  );
}
